import InfoModel from './infoModel'
import * as Utils from '@/assets/script/utils'
import { CONSTANT } from '@/constant/index'

class ChatModel {
  constructor(option) {
    this.isLoading = false
    this.infos = []
  }

  add(config) {
    return new Promise(resolve => {
      const info = new InfoModel(config)
      this.infos.push(info)
      resolve(info)
    })
  }

  remove(id) {
    this.isRemove = true
    this.infos.forEach((info, index) => {
      if (info.id === id) {
        this.infos.splice(index, 1)
      }
    })
  }

  clear() {
    this.infos = []
  }

  unique() {
    const { infos } = this
    infos.forEach((info, index) => {
      if (index > 0 && info.title === infos[index - 1].title) {
        this.infos.splice(index, 1)
      }
    })
  }

  showConnecting(callback) {
    this.connectInfo = new InfoModel({
      type: 'CONNECTING',
      title: CONSTANT.CONNECT_TIP,
      desc: CONSTANT.CONNECT_DESC
    })
    this.infos.push(this.connectInfo)
    callback && callback(this.connectInfo)
  }

  hideConnecting() {
    this.infos.forEach((info, index) => {
      if (info.id === this.connectInfo.id) {
        this.infos.splice(index, 1)
      }
    })
  }

  setInfoTitle({ id, title, type, desc }) {
    this.infos.forEach(item => {
      if (item.id === id) {
        if (title) item.setTitle(title)
        if (type) item.setType(type)
        if (desc) item.setDesc(desc)
      }
    })
  }

  getInfosByImg() {
    return this.infos
      .filter(info => {
        const hasImg = /img\[([^\s]+?)\]/g.test(info.content)
        const isType = info.type.search('IMAGE') > -1
        return hasImg || isType
      })
      .map(item => {
        const isArticle = item.type.search('ARTICLE') > -1
        const content = isArticle ? item.content : item.title
        return {
          id: item.id,
          url: content.replace(/(^img\[)|(\]$)/g, '')
        }
      })
  }

  setRead(ids) {
    ids = ids.split(',')
    this.infos.forEach(info => {
      for (let i = 0; i < ids.length; i++) {
        if (ids[i] === info.id) {
          info.unread = false
        }
      }
    })
  }

  getInfosByReceive() {
    let startIndex = 0
    for (let i = 0; i < this.infos.length; i++) {
      if (this.infos[i].title === CONSTANT.HISTORY_DIVIDING) {
        startIndex = i
      }
    }
    return this.infos.filter((info, index) => {
      const isType = Utils.checkType('RECEIVE & ARTIFICIAL', info.type)
      const isUnAudio = !Utils.checkType('AUDIO', info.type)
      const isNew = index > startIndex
      const isRead = info.unread
      return isType && isUnAudio && isNew && isRead
    })
  }

  setLoading(id, value) {
    const title = ' '
    const desc = CONSTANT.LOADING_TIP.replace(/{}/, value)
    this.setInfoTitle({ id, title, desc })
  }

  escape(context = '') {
    const reg = {
      face: /face\[([^\s\[\]]+?)\]/g,
      img: /img\[([^\s]+?)\]/g,
      href: /a\([\s\S]+?\)\[[\s\S]*?\]/g
    }
    const tokens = []
    const createToken = html => `@@CHAT_TOKEN_${tokens.push(html) - 1}@@`

    const content = String(context)
      .replace(/\r\n?/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(reg.face, face => {
        const html = this.renderFace(face)
        return html ? createToken(html) : face
      })
      .replace(reg.img, img => {
        const html = this.renderImage(img)
        return html ? createToken(html) : img
      })
      .replace(reg.href, str => {
        const html = this.renderCustomLink(str)
        return html ? createToken(html) : str
      })
      .replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (match, attrs, text) => {
        const html = this.renderAllowedAnchor(attrs, text)
        return html ? createToken(html) : match
      })
      .replace(/<(b|strong|span)\b([^>]*)>([\s\S]*?)<\/\1>/gi, (match, tag, attrs, text) => {
        const html = this.renderAllowedInlineTag(tag, attrs, text)
        return html ? createToken(html) : match
      })

    return this.restoreTokens(
      this.renderMarkdown(this.escapeHtml(content)),
      tokens
    )
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/`/g, '&#96;')
  }

  getAttribute(attrs, name) {
    const match = String(attrs || '').match(new RegExp(`${name}\\s*=\\s*["']([^"']*)["']`, 'i'))
    return match ? match[1] : ''
  }

  sanitizeUrl(url, allowDataImage = false) {
    const value = String(url || '').trim()
    if (!value) return ''
    if (/^(https?:|mailto:|tel:)/i.test(value)) return value
    if (allowDataImage && /^data:image\//i.test(value)) return value
    if (/^(\/|\.\/|\.\.\/)/.test(value)) return value
    return ''
  }

  renderFace(face) {
    const alt = face.replace(/^face/g, '')
    const src = Utils.faces[alt]
    if (!src) return ''
    return (
      '<img alt="' + this.escapeAttribute(alt) + '" class="face" src="' +
      this.escapeAttribute(src) + '">'
    )
  }

  renderImage(img) {
    const src = this.sanitizeUrl(img.replace(/(^img\[)|(\]$)/g, ''), true)
    if (!src) return ''
    return (
      '<img class="photo" type="image" src="' +
      this.escapeAttribute(src) + '">'
    )
  }

  renderCustomLink(str) {
    const href = (str.match(/a\(([\s\S]+?)\)\[/) || [])[1]
    const text = (str.match(/\)\[([\s\S]*?)\]/) || [])[1]
    const safeHref = this.sanitizeUrl(href)
    if (!safeHref) return ''
    return (
      '<a class="link" href="' +
      this.escapeAttribute(safeHref) +
      '" target="_blank" rel="noopener noreferrer">' +
      this.escapeHtml(text || safeHref) +
      '</a>'
    )
  }

  renderAllowedAnchor(attrs, text) {
    const href = this.sanitizeUrl(this.getAttribute(attrs, 'href'))
    if (!href) return ''
    const className = this.getAttribute(attrs, 'class')
    const target = /^_self$/i.test(this.getAttribute(attrs, 'target')) ? '_self' : '_blank'
    const classAttr = className ? ` class="${this.escapeAttribute(className)}"` : ''
    const relAttr = target === '_blank' ? ' rel="noopener noreferrer"' : ''
    return (
      `<a${classAttr} href="${this.escapeAttribute(href)}" target="${target}"${relAttr}>` +
      this.escapeHtml(text) +
      '</a>'
    )
  }

  renderAllowedInlineTag(tag, attrs, text) {
    const className = this.getAttribute(attrs, 'class')
    const type = this.getAttribute(attrs, 'type')
    const classAttr = className ? ` class="${this.escapeAttribute(className)}"` : ''
    const typeAttr = type ? ` type="${this.escapeAttribute(type)}"` : ''
    return (
      `<${tag}${classAttr}${typeAttr}>` +
      this.escapeHtml(text) +
      `</${tag}>`
    )
  }

  renderMarkdown(context) {
    const lines = context.split('\n')
    const html = []
    let listTag = ''
    let listItems = []

    const flushList = () => {
      if (!listTag) return
      html.push(`<${listTag}>${listItems.join('')}</${listTag}>`)
      listTag = ''
      listItems = []
    }

    lines.forEach(line => {
      const heading = line.match(/^\s*(#{1,6})\s*(.+?)\s*$/)
      const ordered = line.match(/^\s*\d+\.\s+(.+)$/)
      const unordered = line.match(/^\s*[-*]\s+(.+)$/)

      if (heading) {
        flushList()
        const level = heading[1].length
        html.push(`<h${level}>${this.renderInlineMarkdown(heading[2])}</h${level}>`)
        return
      }

      if (ordered || unordered) {
        const nextListTag = ordered ? 'ol' : 'ul'
        if (listTag && listTag !== nextListTag) {
          flushList()
        }
        listTag = nextListTag
        listItems.push(`<li>${this.renderInlineMarkdown((ordered || unordered)[1])}</li>`)
        return
      }

      flushList()
      html.push(line.trim() ? this.renderInlineMarkdown(line) : '')
    })

    flushList()

    return html
      .join('<br>')
      .replace(/(<br>){3,}/g, '<br><br>')
  }

  renderInlineMarkdown(line) {
    return String(line)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>')
  }

  restoreTokens(context, tokens) {
    return context.replace(/@@CHAT_TOKEN_(\d+)@@/g, (match, index) => tokens[Number(index)] || '')
  }
}

export default ChatModel



// WEBPACK FOOTER //
// ./src/model/chatModel.js